/**
 * Where every bucket and bar of the chart is, worked out from the scales ngx-charts drew it with.
 *
 * The overlays (numbers, totals, month brackets, what-if guides, the hover panel) used to be placed by
 * reading each bar's rectangle back off the page. ngx-charts hides a bar of zero height
 * (display:none), and the browser reports a hidden element's box at 0,0 with no size, so every empty
 * bucket broke something: an empty last week put "Oct 2026" over August (13/10/26), and the hover
 * panel gave an empty day to its neighbours. ngx-charts places its own bars from its scales, as
 * Highcharts and ECharts place their data labels and plot bands, so the overlays do the same.
 *
 * The arithmetic below repeats ngx-charts 20.1's own (BarVerticalStackedComponent, BarVertical2D,
 * LineChartComponent and SeriesVerticalComponent in fesm2015/swimlane-ngx-charts.mjs). It reads the
 * chart's public dims, scales and transform, and returns null rather than guessing if they are not
 * there, so an upgrade that renames them stops the overlays instead of misplacing them.
 */

/** A box in the chart svg's own coordinates. */
export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** One segment of a bucket as the chart draws it. */
export interface GeoSegment {
  name: string;
  value: number;
}

/** One bar position on the axis. */
export interface GeoBucket {
  key: string;
  /** Used by the line chart, whose x axis is time. */
  date: Date | null;
  segments: GeoSegment[];
}

export type ChartStyle = 'stacked' | 'grouped' | 'line';

export interface PlotGeometry {
  style: ChartStyle;
  /** The plot area. */
  plot: Box;
  /**
   * Each bucket's place on the axis, whether or not anything is drawn in it: the bars' extent (for a
   * line, the half-way points to its neighbours), and the slot, which reaches half-way into the gap
   * either side so slots touch.
   */
  columns: { x0: number; x1: number; slot0: number; slot1: number }[];
  /** Each segment's bar, in the order of its bucket's segments. Empty for a line. */
  bars: Box[][];
  /** A value's height in the svg. */
  y(value: number): number;
}

/** "translate(64 , 10)" to [64, 10]. */
function parseTranslate(transform: string | undefined): [number, number] | null {
  const m = /translate\(\s*(-?[\d.]+)\s*,?\s*(-?[\d.]+)\s*\)/.exec(transform || '');
  return m ? [Number(m[1]), Number(m[2])] : null;
}

const isScale = (s: any) => typeof s === 'function' && typeof s.domain === 'function';

/**
 * The geometry of an ngx-charts bar-vertical-stacked, bar-vertical-2d or line-chart instance, for the
 * buckets it was given in this order. Null when the instance has not laid itself out yet.
 */
export function plotGeometry(chart: any, style: ChartStyle, buckets: GeoBucket[]): PlotGeometry | null {
  if (!chart?.dims || !buckets.length) return null;
  const origin = parseTranslate(chart.transform);
  if (!origin) return null;
  const [ox, oy] = origin;
  const { width, height } = chart.dims as { width: number; height: number };
  if (!(width > 0) || !(height > 0)) return null;

  const plot: Box = { left: ox, top: oy, width, height };

  if (style === 'line') return lineGeometry(chart, buckets, plot);

  const xScale = style === 'grouped' ? chart.groupScale : chart.xScale;
  const yScale = style === 'grouped' ? chart.valueScale : chart.yScale;
  if (!isScale(xScale) || !isScale(yScale) || typeof xScale.bandwidth !== 'function') return null;

  const y = (v: number) => oy + yScale(v);
  const step: number = xScale.step();
  const bandwidth: number = xScale.bandwidth();
  const half = (step - bandwidth) / 2;

  const columns: PlotGeometry['columns'] = [];
  const bars: Box[][] = [];

  for (const b of buckets) {
    const at = xScale(b.key);
    if (at === undefined || at === null || isNaN(at)) return null;
    const x0 = ox + at;

    if (style === 'stacked') {
      // SeriesVerticalComponent, stacked: each segment sits on the running total of its own sign.
      const w = Math.round(bandwidth);
      const offset = { pos: 0, neg: 0 };
      bars.push(b.segments.map(s => {
        const sign = s.value > 0 ? 'pos' : 'neg';
        const from = offset[sign];
        const to = from + s.value;
        offset[sign] = to;
        const top = Math.min(y(from), y(to));
        return { left: x0, top, width: w, height: Math.abs(y(from) - y(to)) };
      }));
      columns.push({ x0, x1: x0 + w, slot0: x0 - half, slot1: x0 + bandwidth + half });
    } else {
      // SeriesVerticalComponent, standard: each series in its own place inside the group, from zero.
      const inner = chart.innerScale;
      if (!isScale(inner) || typeof inner.bandwidth !== 'function') return null;
      const w = Math.round(inner.bandwidth());
      const base = Math.max(yScale.domain()[0], 0);
      bars.push(b.segments.map(s => {
        const ix = inner(s.name);
        const left = x0 + (ix ?? 0);
        const top = s.value < 0 ? y(0) : y(s.value);
        return { left, top, width: ix === undefined ? 0 : w, height: Math.abs(y(s.value) - y(base)) };
      }));
      columns.push({ x0, x1: x0 + bandwidth, slot0: x0 - half, slot1: x0 + bandwidth + half });
    }
  }

  return { style, plot, columns, bars, y };
}

/** A line chart's buckets are points on a time axis, so each one's slot runs half-way to its neighbours. */
function lineGeometry(chart: any, buckets: GeoBucket[], plot: Box): PlotGeometry | null {
  const xScale = chart.xScale, yScale = chart.yScale;
  if (!isScale(xScale) || !isScale(yScale)) return null;

  const xs = buckets.map(b => {
    const v = xScale(b.date ?? b.key);
    return v === undefined || v === null || isNaN(v) ? NaN : plot.left + v;
  });
  if (xs.some(isNaN)) return null;

  const columns = xs.map((x, i) => {
    const prev = i > 0 ? xs[i - 1] : NaN;
    const next = i < xs.length - 1 ? xs[i + 1] : NaN;
    const left = !isNaN(prev) ? (x + prev) / 2 : !isNaN(next) ? x - (next - x) / 2 : plot.left;
    const right = !isNaN(next) ? (x + next) / 2 : !isNaN(prev) ? x + (x - prev) / 2 : plot.left + plot.width;
    return { x0: left, x1: right, slot0: left, slot1: right };
  });

  return { style: 'line', plot, columns, bars: buckets.map(() => []), y: (v: number) => plot.top + yScale(v) };
}

/** The bucket under a point across the axis, or -1. Empty buckets count: every slot is a bucket. */
export function bucketAt(geo: PlotGeometry, x: number): number {
  const cols = geo.columns;
  if (!cols.length) return -1;
  // A little grace beyond the outer edges, where the pointer leaves the last bar.
  if (x < cols[0].slot0 - 4 || x > cols[cols.length - 1].slot1 + 4) return -1;
  let lo = 0, hi = cols.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (cols[mid].slot0 <= x) lo = mid; else hi = mid - 1;
  }
  return lo;
}
